<template>
  <div class="sci-view-container">
    <!-- Top Glassmorphic Navigation -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('gestionale') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-sliders-h me-2 text-primary"></i>
            {{ $t('impostazioni') || 'Impostazioni Generali & Fiscale' }}
          </h1>
          <p class="sci-subtitle">Configurazione del locale, giorni di chiusura, ferie, delivery, scontrino e pagamenti</p>
        </div>
      </div>
      <div class="sci-header-right">
        <button class="sci-btn sci-btn-success" @click="saveSettings" :disabled="isSaving">
          <i class="fas fa-save me-1" :class="{ 'fa-spin': isSaving }"></i>
          <span>{{ isSaving ? 'Salvataggio...' : 'Salva Tutte le Impostazioni' }}</span>
        </button>
      </div>
    </header>

    <!-- Feedback Floating Toast -->
    <transition name="fade">
      <div class="sci-floating-toast" v-if="feedbackMsg">
        <i class="fas fa-check-circle text-success me-2"></i>
        <span>{{ feedbackMsg }}</span>
      </div>
    </transition>

    <!-- Main Spatial Bento Grid -->
    <main class="sci-main-content">
      <!-- Category Tabs (Hick-Hyman Progressive Disclosure) -->
      <div class="settings-tabs-bar mb-4">
        <button 
          type="button" 
          class="settings-tab-btn" 
          :class="{ active: activeTab === 'calendar' }"
          @click="activeTab = 'calendar'"
        >
          <i class="fas fa-calendar-alt me-2"></i> Calendario & Locale
        </button>
        <button 
          type="button" 
          class="settings-tab-btn" 
          :class="{ active: activeTab === 'delivery' }"
          @click="activeTab = 'delivery'"
        >
          <i class="fas fa-motorcycle me-2"></i> Canali Delivery
        </button>
        <button 
          type="button" 
          class="settings-tab-btn" 
          :class="{ active: activeTab === 'fiscal' }"
          @click="activeTab = 'fiscal'"
        >
          <i class="fas fa-file-invoice-dollar me-2"></i> Fiscale & Scontrino
        </button>
        <button 
          type="button" 
          class="settings-tab-btn" 
          :class="{ active: activeTab === 'payments' }"
          @click="activeTab = 'payments'"
        >
          <i class="fab fa-stripe me-2"></i> Pagamenti Stripe
        </button>
      </div>

      <!-- TAB 1: CALENDARIO & LOCALE -->
      <section v-show="activeTab === 'calendar'" class="tab-pane-content">
        <!-- Giorni di Chiusura -->
        <div class="sci-card mb-4">
          <div class="card-section-header">
            <div>
              <h3 class="section-title">
                <i class="fas fa-door-closed me-2 text-primary"></i>
                Giorni di Chiusura Settimanale
              </h3>
              <p class="section-desc">I giorni selezionati risulteranno non prenotabili e non disponibili per l'asporto</p>
            </div>
          </div>

          <div class="days-chips-grid">
            <label 
              v-for="(giorno, index) in giorniSettimana" 
              :key="index"
              class="day-chip"
              :class="{ 'day-selected': settings.giorniChiusura.includes(index) }"
            >
              <input 
                type="checkbox" 
                class="d-none" 
                :value="index" 
                v-model="settings.giorniChiusura" 
              />
              <span class="day-check-icon">
                <i class="fas" :class="settings.giorniChiusura.includes(index) ? 'fa-check' : 'fa-plus'"></i>
              </span>
              <span class="day-name">{{ giorno }}</span>
            </label>
          </div>
        </div>

        <!-- Ferie -->
        <div class="sci-card mb-4">
          <div class="card-section-header d-flex justify-content-between align-items-center">
            <div>
              <h3 class="section-title">
                <i class="fas fa-umbrella-beach me-2 text-amber"></i>
                Periodi di Ferie & Chiusura Straordinaria
              </h3>
              <p class="section-desc">Intervalli in cui il locale è completamente chiuso al pubblico</p>
            </div>
            <button class="sci-btn sci-btn-secondary" @click="addFerie">
              <i class="fas fa-plus me-1 text-primary"></i> Aggiungi Periodo
            </button>
          </div>

          <div v-if="settings.ferie.length > 0" class="ferie-list-wrap mt-3">
            <div 
              v-for="(periodo, i) in settings.ferie" 
              :key="i"
              class="ferie-row-card"
            >
              <div class="ferie-dates-group">
                <div class="ferie-input-box">
                  <span class="ferie-label">Dal</span>
                  <input type="date" class="sci-input" v-model="periodo.da" />
                </div>
                <div class="ferie-arrow">
                  <i class="fas fa-arrow-right"></i>
                </div>
                <div class="ferie-input-box">
                  <span class="ferie-label">Al</span>
                  <input type="date" class="sci-input" v-model="periodo.a" />
                </div>
              </div>

              <button class="btn-delete-row" title="Rimuovi Periodo" @click="removeFerie(i)">
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>
          <p v-else class="text-muted mt-2 mb-0">Nessun periodo di ferie programmato.</p>
        </div>

        <!-- Parametri Base: Coperto e Password Admin -->
        <div class="bento-split-row">
          <div class="sci-card">
            <h3 class="section-title">
              <i class="fas fa-utensils me-2 text-primary"></i>
              Costo Coperto al Tavolo
            </h3>
            <p class="section-desc">Applicato per persona nei tavoli serviti in sala</p>
            <div class="sci-currency-field mt-3">
              <span class="currency-symbol">€</span>
              <input 
                type="number" 
                step="0.50" 
                min="0" 
                class="sci-currency-input font-monospace" 
                v-model.number="settings.costoCoperto" 
              />
            </div>
          </div>

          <div class="sci-card">
            <h3 class="section-title">
              <i class="fas fa-shield-alt me-2 text-danger"></i>
              Password di Protezione Admin
            </h3>
            <p class="section-desc">Richiesta per annullamento ordini, sconti e operazioni critiche</p>
            <div class="sci-input-wrap mt-3">
              <i class="fas fa-key input-icon"></i>
              <input 
                type="password" 
                class="sci-input font-monospace" 
                v-model="settings.passwordAdmin" 
                placeholder="Lascia vuoto o inserisci nuova password"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- TAB 2: DELIVERY -->
      <section v-show="activeTab === 'delivery'" class="tab-pane-content">
        <div class="sci-card mb-4">
          <div class="card-section-header">
            <div>
              <h3 class="section-title">
                <i class="fas fa-motorcycle me-2 text-primary"></i>
                Piattaforme di Delivery Convenzionate
              </h3>
              <p class="section-desc">Configura JustEat, Glovo, Deliveroo, ecc. con le rispettive commissioni percentuali e quote fisse per ordine</p>
            </div>
          </div>

          <!-- Form Nuovo Delivery -->
          <div class="add-delivery-banner mb-4">
            <h4 class="banner-title"><i class="fas fa-plus-circle me-1 text-primary"></i> Aggiungi Nuova Piattaforma</h4>
            <div class="add-delivery-grid mt-2">
              <div class="sci-input-group">
                <label class="sci-label">Nome Piattaforma</label>
                <input type="text" class="sci-input" v-model="newDelivery.nome" placeholder="Es. JustEat" />
              </div>
              <div class="sci-input-group">
                <label class="sci-label">Commissione (%)</label>
                <input type="number" step="0.1" min="0" max="100" class="sci-input font-monospace" v-model.number="newDelivery.percentuale" placeholder="15" />
              </div>
              <div class="sci-input-group">
                <label class="sci-label">Quota Fissa (€/ordine)</label>
                <input type="number" step="0.10" min="0" class="sci-input font-monospace" v-model.number="newDelivery.quotaFissa" placeholder="0.50" />
              </div>
              <button 
                class="sci-btn sci-btn-primary align-self-end" 
                @click="addDelivery" 
                :disabled="!newDelivery.nome.trim()"
              >
                + Aggiungi Piattaforma
              </button>
            </div>
          </div>

          <!-- Existing Delivery Platforms Table -->
          <div class="table-responsive" v-if="settings.delivery && settings.delivery.length > 0">
            <table class="sci-table">
              <thead>
                <tr>
                  <th>Nome Piattaforma</th>
                  <th style="width: 200px;">Commissione (%)</th>
                  <th style="width: 200px;">Quota Fissa (€)</th>
                  <th style="width: 100px; text-align: center;">Azioni</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in settings.delivery" :key="idx" class="sci-table-row">
                  <td>
                    <input type="text" class="sci-input" v-model="item.nome" />
                  </td>
                  <td>
                    <div class="sci-input-wrap">
                      <input type="number" step="0.1" min="0" max="100" class="sci-input font-monospace" v-model.number="item.percentuale" />
                      <span class="input-suffix">%</span>
                    </div>
                  </td>
                  <td>
                    <div class="sci-input-wrap">
                      <input type="number" step="0.10" min="0" class="sci-input font-monospace" v-model.number="item.quotaFissa" />
                      <span class="input-suffix">€</span>
                    </div>
                  </td>
                  <td style="text-align: center;">
                    <button class="btn-delete-row" title="Rimuovi Piattaforma" @click="removeDelivery(idx)">
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-muted mb-0">Nessuna piattaforma delivery inserita.</p>
        </div>
      </section>

      <!-- TAB 3: FISCALE & SCONTRINO -->
      <section v-show="activeTab === 'fiscal'" class="tab-pane-content">
        <!-- Scontrino Elettronico Toggle & Credentials -->
        <div class="sci-card mb-4">
          <div class="card-section-header d-flex justify-content-between align-items-center">
            <div>
              <h3 class="section-title">
                <i class="fas fa-receipt me-2 text-success"></i>
                Registratore Telematico & Scontrino Elettronico
              </h3>
              <p class="section-desc">Integrazione API con RT accreditato Agenzia delle Entrate</p>
            </div>
            <div class="switch-wrap">
              <label class="sci-switch">
                <input type="checkbox" v-model="settings.scontrinoElettronico.isActive" />
                <span class="slider round"></span>
              </label>
              <span class="switch-label fw-bold ms-2">
                {{ settings.scontrinoElettronico.isActive ? 'Attivo' : 'Disattivato' }}
              </span>
            </div>
          </div>

          <div class="row g-3 mt-2" v-if="settings.scontrinoElettronico.isActive">
            <div class="col-md-6">
              <label class="sci-label">API Key RT</label>
              <input type="password" class="sci-input font-monospace" v-model="settings.scontrinoElettronico.apiKey" placeholder="Inserisci API Key" />
            </div>
            <div class="col-md-6">
              <label class="sci-label">Password RT</label>
              <input type="password" class="sci-input font-monospace" v-model="settings.scontrinoElettronico.password" placeholder="Inserisci Password" />
            </div>
          </div>
        </div>

        <!-- Dati Aziendali per Fatturazione -->
        <div class="sci-card mb-4">
          <div class="card-section-header">
            <div>
              <h3 class="section-title">
                <i class="fas fa-building me-2 text-primary"></i>
                Dati Aziendali per Fatturazione Elettronica
              </h3>
              <p class="section-desc">Inseriti automaticamente nell'intestazione di fatture e documenti fiscali</p>
            </div>
          </div>

          <div class="row g-3 mt-2">
            <div class="col-md-6">
              <label class="sci-label">Ragione Sociale / Denominazione *</label>
              <input type="text" class="sci-input" v-model="settings.datiAzienda.denominazione" />
            </div>
            <div class="col-md-6">
              <label class="sci-label">Partita IVA *</label>
              <input type="text" class="sci-input font-monospace" v-model="settings.datiAzienda.pIva" />
            </div>
            <div class="col-md-6">
              <label class="sci-label">Codice Fiscale</label>
              <input type="text" class="sci-input font-monospace" v-model="settings.datiAzienda.codFiscale" />
            </div>
            <div class="col-md-6">
              <label class="sci-label">Regime Fiscale (es. RF01) *</label>
              <input type="text" class="sci-input font-monospace" v-model="settings.datiAzienda.regimeFiscale" placeholder="RF01" />
            </div>
            <div class="col-md-12">
              <label class="sci-label">Indirizzo Sede Legale *</label>
              <input type="text" class="sci-input" v-model="settings.datiAzienda.indirizzo" />
            </div>
            <div class="col-md-4">
              <label class="sci-label">CAP *</label>
              <input type="text" class="sci-input font-monospace" v-model="settings.datiAzienda.cap" />
            </div>
            <div class="col-md-6">
              <label class="sci-label">Comune *</label>
              <input type="text" class="sci-input" v-model="settings.datiAzienda.comune" />
            </div>
            <div class="col-md-2">
              <label class="sci-label">Provincia *</label>
              <input type="text" class="sci-input text-uppercase font-monospace" maxlength="2" v-model="settings.datiAzienda.provincia" />
            </div>
          </div>
        </div>
      </section>

      <!-- TAB 4: PAGAMENTI & STRIPE -->
      <section v-show="activeTab === 'payments'" class="tab-pane-content">
        <div class="sci-card mb-4">
          <div class="card-section-header">
            <div>
              <h3 class="section-title">
                <i class="fab fa-stripe me-2 text-primary" style="font-size: 1.5rem;"></i>
                Configurazione Stripe Checkout
              </h3>
              <p class="section-desc">Abilita il pagamento digitale con carta di credito e Apple/Google Pay per gli ordini d'asporto e tavoli</p>
            </div>
          </div>

          <div class="row g-3 mt-2">
            <div class="col-md-12">
              <label class="sci-label">Stripe Secret Key (sk_live_... o sk_test_...)</label>
              <div class="sci-input-wrap">
                <i class="fas fa-key input-icon"></i>
                <input 
                  type="password" 
                  class="sci-input font-monospace" 
                  v-model="settings.stripeSecretKey" 
                  placeholder="sk_test_..." 
                />
              </div>
            </div>
            <div class="col-md-12">
              <label class="sci-label">Stripe Webhook Secret (whsec_...)</label>
              <div class="sci-input-wrap">
                <i class="fas fa-lock input-icon"></i>
                <input 
                  type="password" 
                  class="sci-input font-monospace" 
                  v-model="settings.stripeWebhookSecret" 
                  placeholder="whsec_..." 
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Bottom Save Action Bar -->
      <div class="settings-bottom-bar mt-4">
        <span class="text-muted small">
          Tutte le modifiche diventeranno immediatamente operative dopo il salvataggio.
        </span>
        <button class="sci-btn sci-btn-success" @click="saveSettings" :disabled="isSaving">
          <i class="fas fa-save me-1" :class="{ 'fa-spin': isSaving }"></i>
          <span>{{ isSaving ? 'Salvataggio...' : 'Salva Impostazioni' }}</span>
        </button>
      </div>
    </main>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'Impostazioni',
  data() {
    return {
      store,
      settingsId: '69c90e57851daafaf559b837',
      activeTab: 'calendar',
      isSaving: false,
      feedbackMsg: '',
      giorniSettimana: [
        "Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"
      ],
      newDelivery: {
        nome: '',
        percentuale: null,
        quotaFissa: null
      },
      settings: {
        giorniChiusura: [],
        ferie: [],
        delivery: [],
        stripeSecretKey: '',
        stripeWebhookSecret: '',
        passwordAdmin: '',
        costoCoperto: 0,
        scontrinoElettronico: {
          isActive: false,
          apiKey: '',
          password: ''
        },
        datiAzienda: {
          denominazione: '',
          pIva: '',
          codFiscale: '',
          regimeFiscale: '',
          indirizzo: '',
          cap: '',
          comune: '',
          provincia: ''
        }
      }
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return localStorage.getItem('token') || '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (localStorage.getItem('token') || '');
      } catch (e) {
        return localStorage.getItem('token') || '';
      }
    },
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    addFerie() {
      this.settings.ferie.push({ da: '', a: '' });
      this.showToast('Nuovo periodo di ferie aggiunto');
    },
    removeFerie(index) {
      this.settings.ferie.splice(index, 1);
    },
    addDelivery() {
      if (!this.newDelivery.nome.trim()) return;
      this.settings.delivery.push({
        nome: this.newDelivery.nome.trim(),
        percentuale: this.newDelivery.percentuale !== null ? Number(this.newDelivery.percentuale) : 0,
        quotaFissa: this.newDelivery.quotaFissa !== null ? Number(this.newDelivery.quotaFissa) : 0
      });
      this.newDelivery = { nome: '', percentuale: null, quotaFissa: null };
      this.showToast('Piattaforma delivery aggiunta');
    },
    removeDelivery(index) {
      this.settings.delivery.splice(index, 1);
    },
    async loadSettings() {
      try {
        const token = this.getToken();
        const authHeader = `Bearer ${token}`;

        const res = await this.axios.get(this.store.path + 'impostazioni', {
          headers: {
            authorization: authHeader
          }
        });
        if (res.data && res.data.impostazioni) {
          const imp = res.data.impostazioni;
          this.settingsId = imp._id || this.settingsId;
          this.settings.giorniChiusura = imp.giorniChiusura || [];
          this.settings.ferie = imp.ferie || [];
          this.settings.delivery = imp.delivery || [];
          this.settings.stripeSecretKey = imp.stripeSecretKey || '';
          this.settings.stripeWebhookSecret = imp.stripeWebhookSecret || '';
          this.settings.passwordAdmin = imp.passwordAdmin || '';
          this.settings.costoCoperto = imp.costoCoperto !== undefined ? imp.costoCoperto : 0;
          
          if (imp.scontrinoElettronico) {
            this.settings.scontrinoElettronico.isActive = !!imp.scontrinoElettronico.isActive;
            this.settings.scontrinoElettronico.apiKey = imp.scontrinoElettronico.apiKey || '';
            this.settings.scontrinoElettronico.password = imp.scontrinoElettronico.password || '';
          }

          if (imp.datiAzienda) {
            this.settings.datiAzienda = {
              denominazione: imp.datiAzienda.denominazione || '',
              pIva: imp.datiAzienda.pIva || '',
              codFiscale: imp.datiAzienda.codFiscale || '',
              regimeFiscale: imp.datiAzienda.regimeFiscale || '',
              indirizzo: imp.datiAzienda.indirizzo || '',
              cap: imp.datiAzienda.cap || '',
              comune: imp.datiAzienda.comune || '',
              provincia: imp.datiAzienda.provincia || ''
            };
          }
        }
      } catch (err) {
        console.error("Errore nel caricamento impostazioni:", err);
      }
    },
    async saveSettings() {
      this.isSaving = true;
      try {
        const token = this.getToken();
        const authHeader = `Bearer ${token}`;

        await this.axios.post(this.store.path + 'impostazioni', {
          id: this.settingsId,
          settings: this.settings,
          headers: {
            authorization: authHeader
          }
        }, {
          headers: {
            authorization: authHeader
          }
        });
        this.showToast('Impostazioni salvate con successo!');
      } catch (err) {
        console.error("Errore nel salvataggio impostazioni:", err);
        alert("Errore durante il salvataggio delle impostazioni.");
      } finally {
        this.isSaving = false;
      }
    },
    showToast(msg) {
      this.feedbackMsg = msg;
      setTimeout(() => {
        if (this.feedbackMsg === msg) this.feedbackMsg = '';
      }, 2500);
    }
  },
  mounted() {
    this.loadSettings();
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 60px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 12px;
  color: var(--sci-text-muted, #475569);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  color: var(--sci-text-main, #0f172a);
  border-color: #cbd5e1;
}

.sci-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.82rem;
  color: var(--sci-text-muted, #475569);
}

.sci-btn-success {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 20px;
  background: #059669;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.sci-btn-success:hover:not(:disabled) {
  background: #047857;
}

.sci-btn-secondary {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
}

.sci-main-content {
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  padding: 24px 20px;
}

/* Tabs */
.settings-tabs-bar {
  display: flex;
  gap: 8px;
  background: #ffffff;
  padding: 6px;
  border-radius: 14px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  flex-wrap: wrap;
}

.settings-tab-btn {
  flex: 1;
  min-height: 44px;
  border: none;
  background: transparent;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--sci-text-muted, #475569);
  cursor: pointer;
  transition: all 0.2s;
}

.settings-tab-btn:hover {
  background: #f8fafc;
  color: var(--sci-text-main, #0f172a);
}

.settings-tab-btn.active {
  background: var(--sci-primary, #4f46e5);
  color: #ffffff;
}

/* Card */
.sci-card {
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 18px;
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.05);
  padding: 24px;
}

.card-section-header {
  margin-bottom: 18px;
}

.section-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
}

.section-desc {
  margin: 4px 0 0 0;
  font-size: 0.85rem;
  color: var(--sci-text-muted, #475569);
}

/* Days Chips */
.days-chips-grid {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.day-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 16px;
  background: #f8fafc;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--sci-text-muted, #475569);
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
}

.day-chip:hover {
  background: #f1f5f9;
}

.day-chip.day-selected {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #b91c1c;
}

.day-check-icon {
  font-size: 0.8rem;
}

/* Ferie */
.ferie-row-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #f8fafc;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid var(--sci-border, #e2e8f0);
  margin-bottom: 10px;
}

.ferie-dates-group {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ferie-input-box {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ferie-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--sci-text-muted, #475569);
}

.ferie-arrow {
  color: #94a3b8;
}

.btn-delete-row {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #fee2e2;
  background: #fff5f5;
  color: #ef4444;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-delete-row:hover {
  background: #ef4444;
  color: #ffffff;
}

.bento-split-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

@media (max-width: 768px) {
  .bento-split-row {
    grid-template-columns: 1fr;
  }
}

.add-delivery-banner {
  background: #f8fafc;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 14px;
  padding: 18px;
}

.banner-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
}

.add-delivery-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr auto;
  gap: 14px;
}

@media (max-width: 768px) {
  .add-delivery-grid {
    grid-template-columns: 1fr;
  }
}

/* Inputs */
.sci-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--sci-text-muted, #475569);
  margin-bottom: 6px;
}

.sci-input {
  width: 100%;
  height: 42px;
  padding: 0 14px;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 10px;
  font-size: 0.9rem;
  color: var(--sci-text-main, #0f172a);
  background: #ffffff;
  outline: none;
  transition: border-color 0.2s;
}

.sci-input:focus {
  border-color: var(--sci-primary, #4f46e5);
}

.sci-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 14px;
  color: #94a3b8;
}

.sci-input-wrap .sci-input {
  padding-left: 38px;
}

.input-suffix {
  position: absolute;
  right: 14px;
  color: #94a3b8;
  font-weight: 700;
}

.sci-currency-field {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 220px;
}

.currency-symbol {
  position: absolute;
  left: 14px;
  font-weight: 700;
  color: #64748b;
}

.sci-currency-input {
  width: 100%;
  height: 42px;
  padding: 0 14px 0 32px;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: 700;
  outline: none;
}

/* Switch */
.sci-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
}

.sci-switch input { opacity: 0; width: 0; height: 0; }

.slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #cbd5e1;
  transition: .3s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .3s;
}

input:checked + .slider { background-color: #059669; }
input:checked + .slider:before { transform: translateX(22px); }
.slider.round { border-radius: 34px; }
.slider.round:before { border-radius: 50%; }

.switch-wrap {
  display: flex;
  align-items: center;
}

.settings-bottom-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  padding: 16px 24px;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
}

.sci-table {
  width: 100%;
  border-collapse: collapse;
}

.sci-table th {
  padding: 12px 14px;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--sci-text-muted, #475569);
  border-bottom: 2px solid var(--sci-border, #e2e8f0);
}

.sci-table-row td {
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
}

.sci-floating-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  align-items: center;
  background: #0f172a;
  color: #ffffff;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  font-weight: 600;
  font-size: 0.9rem;
}
</style>
